import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import { useTranslation } from 'react-i18next';
import { grayColor, whiteColor } from 'assets/jss/material-dashboard-pro-react';

const defaultStyles = {
  control: styles => ({
    ...styles,
    border: 'none',
    borderRadius: '0',
    borderBottom: '1px solid #d2d2d2',
    backgroundColor: 'white',
    boxShadow: 'none',
    minHeight: '32px',
    '&:hover': {
      borderColor: 'none',
      cursor: 'pointer',
    },
  }),
  dropdownIndicator: styles => ({
    ...styles,
    padding: '5px',
  }),
  clearIndicator: styles => ({
    ...styles,
    padding: '5px',
  }),
  multiValueLabel: styles => ({
    ...styles,
    padding: '2px',
  }),
  indicatorSeparator: styles => ({
    ...styles,
    display: 'none',
  }),
  option: (styles, { isFocused, isDisabled }) => ({
    ...styles,
    textAlign: 'start',
    backgroundColor: isFocused ? '#9c27b0' : 'white',
    '& label': {
      color: isFocused ? whiteColor : grayColor[7],
    },
    ':active': {
      backgroundColor: '#9c27b0',
    },
    ...(isDisabled
      ? {
          pointerEvents: 'none',
          borderTop: '1px solid #d3d3d3',
          margin: '8px 7% 0 7%',
          width: '86%',
          padding: '8px 0',
          '& label': {
            textTransform: 'uppercase',
            fontSize: '12px',
            fontWeight: '400',
            color: '#8a8a8a',
            backgroundColor: 'white',
          },
        }
      : {}),
  }),
  multiValue: styles => ({
    ...styles,
    borderRadius: '9px',
  }),
  multiValueRemove: styles => ({
    ...styles,
    ':hover': {
      ...styles,
      borderRadius: '0 7px 7px 0',
      cursor: 'pointer',
    },
  }),
};

const getSelectStyles = menuWidth => {
  const parsedMenuWidth =
    typeof menuWidth === 'number' ? `${menuWidth}px` : menuWidth;

  if (!parsedMenuWidth) {
    return defaultStyles;
  }

  return {
    ...defaultStyles,
    menu: styles => ({
      ...styles,
      width: parsedMenuWidth,
      minWidth: parsedMenuWidth,
    }),
  };
};

const MySelect = props => {
  const { t } = useTranslation();
  const localProps = { ...props };
  const styles = getSelectStyles(localProps.menuWidth);

  const defaultProps = {
    allOption: {
      label: t('common.shared.checkAll'),
      value: '*',
    },
  };
  localProps.allOption = localProps.allOption || defaultProps.allOption;

  if (localProps.allowSelectAll) {
    return (
      <ReactSelect
        noOptionsMessage={() => t('$*noSearchResults')}
        {...localProps}
        isOptionDisabled={option => option.isSeparator}
        styles={styles}
        options={[localProps.allOption, ...localProps.options]}
        onChange={(selected, event) => {
          if (selected !== null && selected.length > 0) {
            if (
              selected[selected.length - 1].value === localProps.allOption.value
            ) {
              return localProps.onChange([
                localProps.allOption,
                ...localProps.options,
              ]);
            }
            let result = [];
            if (selected.length === localProps.options.length) {
              if (
                selected.some(
                  selectedItem =>
                    selectedItem.value === localProps.allOption.value
                )
              ) {
                result = selected.filter(
                  option => option.value !== localProps.allOption.value
                );
              } else if (event.action === 'select-option') {
                result = [localProps.allOption, ...localProps.options];
              }
              return localProps.onChange(result);
            }
          }

          return localProps.onChange(selected);
        }}
      />
    );
  }

  return <ReactSelect {...localProps} styles={styles} />;
};

MySelect.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  allowSelectAll: PropTypes.bool,
  isClearable: PropTypes.bool,
  allOption: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default MySelect;
